import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import type {
  Zone,
  CreateZonePayload,
  PaginatedResponse,
} from '@/lib/types';

export interface ZoneFilters {
  page?: number;
  page_size?: number;
  location?: number;
  parent_zone?: number | null;
  q?: string;
  ordering?: string;
}

export function useZones(filters: ZoneFilters = {}, enabled = true) {
  const cleanFilters = Object.fromEntries(
    Object.entries(filters)
      .map(([key, value]) => (key === 'parent_zone' && value === null ? [key, ''] : [key, value]))
      .filter(([key, value]) => {
        if (key === 'parent_zone') return value !== undefined;
        return value !== '' && value !== undefined && value !== null;
      })
  );

  return useQuery({
    queryKey: queryKeys.zones.list(cleanFilters),
    queryFn: async (): Promise<PaginatedResponse<Zone>> => {
      const response = await apiClient.get<PaginatedResponse<Zone>>(
        '/api/v1/cf-zones/',
        { params: cleanFilters }
      );
      return response.data;
    },
    enabled: enabled && (!filters.location || filters.location > 0),
  });
}

export function useCreateZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateZonePayload): Promise<Zone> => {
      const response = await apiClient.post<Zone>('/api/v1/cf-zones/', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.zones.all });
    },
  });
}

export function useUpdateZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<CreateZonePayload>;
    }): Promise<Zone> => {
      const response = await apiClient.patch<Zone>(`/api/v1/cf-zones/${id}/`, data);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.zones.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.zones.detail(variables.id) });
    },
  });
}

export function useDeleteZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await apiClient.delete(`/api/v1/cf-zones/${id}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.zones.all });
    },
  });
}
